import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Content, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { DkimSelector } from '../../../types/models/DkimSelector';
import type { MailDomainDnsZoneState } from '../../../types/models/MailDomain';
import type { ApiResponseDkimKeyDelete } from '../../../types/api/ApiResponseDkimKey';
import type { ApiResponseMailDomainDnsZone } from '../../../types/api/ApiResponseMailDomain';
import type { ApiResponseDnsRrsetDelete } from '../../../types/api/ApiResponseDomainName';

interface DkimDeleteModalProps {
  /** The selector to delete; `null` keeps the modal closed. */
  selector: DkimSelector | null;
  onClose: () => void;
  mailDomainId: number;
  /** Bridge to the DNS side — lets us also remove the published TXT record when the zone is managed. */
  organizationDomainId: number;
  domainName: string;
  /** Refreshes the selectors table after a key is removed. */
  onSuccess: () => void;
}

function DkimDeleteModal({
  selector,
  onClose,
  mailDomainId,
  organizationDomainId,
  domainName,
  onSuccess,
}: DkimDeleteModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // DNS zone state — drives whether we can also remove the published TXT record automatically.
  const [dnsZone, setDnsZone] = useState<MailDomainDnsZoneState | null>(null);
  const [dnsZoneLoading, setDnsZoneLoading] = useState(false);
  const [removeRrset, setRemoveRrset] = useState(true);

  const canRemoveRrset = dnsZone !== null && dnsZone.zone_state === 'configured' && dnsZone.domain_name_id !== null;

  const fetchDnsZone = useCallback(async () => {
    setDnsZoneLoading(true);
    try {
      const response = await apiFetch(`/emails/domains/dns-zone?organization_domain_id=${organizationDomainId}`);
      const body = (await response.json().catch(() => null)) as ApiResponseMailDomainDnsZone | null;
      if (!response.ok || body?.status !== 'success' || !body.data?.dns_zone) {
        setDnsZone(null);
        return;
      }
      setDnsZone(body.data.dns_zone);
    } catch {
      setDnsZone(null);
    } finally {
      setDnsZoneLoading(false);
    }
  }, [apiFetch, organizationDomainId]);

  // Probe the zone state whenever the modal opens (a selector is chosen); re-default the checkbox.
  useEffect(() => {
    if (selector === null) {
      return;
    }
    setRemoveRrset(true);
    void fetchDnsZone();
  }, [selector, fetchDnsZone]);

  const resetAndClose = () => {
    setError(null);
    setIsSubmitting(false);
    setDnsZone(null);
    setDnsZoneLoading(false);
    setRemoveRrset(true);
    onClose();
  };

  // Best-effort removal of the selector's published TXT record. Returns true if a set was actually
  // removed, false if none existed ("No such…" — the no-orphan goal is already met); throws otherwise.
  const deleteRrset = async (domainNameId: number, name: string): Promise<boolean> => {
    const response = await apiFetch(`/datacenters/domain-names/${domainNameId}/dns/rrset`, {
      method: 'DELETE',
      headers: { Accept: 'application/json' },
      body: JSON.stringify({ name, type: 'TXT' }),
    });
    const body = (await response.json().catch(() => null)) as ApiResponseDnsRrsetDelete | null;
    if (response.ok && body?.status === 'success' && body.data?.record_set) {
      return true;
    }
    const reason = body?.message ?? '';
    if (reason.toLowerCase().includes('no such dns record set')) {
      return false;
    }
    throw new Error(reason || `Request failed with status ${response.status}`);
  };

  const handleDelete = async () => {
    if (!selector) {
      return;
    }
    setIsSubmitting(true);
    setError(null);

    // Step 1 — delete the DKIM key (primary action; never rolled back once it succeeds).
    let keyExisted = false;
    let signingActive = true;
    try {
      const params = new URLSearchParams({ selector: selector.selector });
      const response = await apiFetch(`/emails/domains/${mailDomainId}/dkim?${params.toString()}`, {
        method: 'DELETE',
      });
      const body = (await response.json().catch(() => null)) as ApiResponseDkimKeyDelete | null;
      if (body?.status === 'success' && body.data?.dkim_key) {
        keyExisted = body.data.dkim_key.existed;
        signingActive = body.data.dkim_key.domain_signing_active;
      } else {
        throw new Error(body?.message ?? 'Failed to delete DKIM key');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setError(message);
      addNotification({
        title: 'DKIM key deletion failed',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      setIsSubmitting(false);
      return;
    }

    // Step 2 — best-effort DNS cleanup (the key is already gone; never fail the whole op on this).
    type DnsOutcome = 'removed' | 'absent' | 'failed' | 'left' | 'manual';
    let dnsOutcome: DnsOutcome = 'manual';
    // The domain_name_id check repeats canRemoveRrset, but TS needs it to narrow number | null → number.
    if (removeRrset && canRemoveRrset && dnsZone?.domain_name_id != null) {
      try {
        dnsOutcome = (await deleteRrset(dnsZone.domain_name_id, selector.key_name)) ? 'removed' : 'absent';
      } catch {
        dnsOutcome = 'failed';
      }
    } else if (canRemoveRrset) {
      dnsOutcome = 'left'; // managed, but the user opted out
    }

    const base = keyExisted
      ? `DKIM selector "${selector.selector}" was removed from ${domainName}.`
      : `No DKIM key for selector "${selector.selector}" existed on ${domainName}; nothing to remove.`;
    const signingNote = keyExisted && !signingActive ? ' This domain no longer signs outgoing mail with DKIM.' : '';
    // DNS notes are phrased self-standing (named, not "it") so they read correctly whether or not a key
    // existed — a stale TXT record can linger even when the key was already gone.
    let dnsNote = '';
    if (dnsOutcome === 'removed') {
      dnsNote = ` The ${selector.key_name} TXT record was also removed from DNS.`;
    } else if (dnsOutcome === 'failed') {
      dnsNote = ` The ${selector.key_name} TXT record couldn't be removed from DNS automatically — remove it manually to avoid an orphaned public key.`;
    } else if (dnsOutcome === 'left') {
      dnsNote = ` The ${selector.key_name} TXT record was left in DNS.`;
    } else if (dnsOutcome === 'manual') {
      dnsNote = ` Remember to remove the ${selector.key_name} TXT record from DNS to avoid an orphaned public key.`;
    }

    const dnsFailed = dnsOutcome === 'failed';
    addNotification({
      title: dnsFailed
        ? keyExisted
          ? 'DKIM key deleted — DNS not cleaned up'
          : 'DKIM key already removed — DNS not cleaned up'
        : keyExisted
          ? 'DKIM key deleted'
          : 'DKIM key already removed',
      srTitle: dnsFailed ? 'Warning alert' : keyExisted ? 'Success alert' : 'Info alert',
      description: `${base}${signingNote}${dnsNote}`,
      variant: dnsFailed ? 'warning' : keyExisted ? 'success' : 'info',
    });
    setDrawerOpen(true);
    resetAndClose();
    onSuccess();
  };

  const renderDnsSection = () => {
    if (dnsZoneLoading) {
      return (
        <div className="pf-v6-u-mt-md">
          <Content component="small">
            <Spinner size="sm" aria-label="Checking DNS zone" /> Checking whether this domain&apos;s DNS is managed
            here…
          </Content>
        </div>
      );
    }
    if (canRemoveRrset) {
      return (
        <div className="pf-v6-u-mt-md">
          <Checkbox
            id="dkim-delete-remove-rrset"
            label={`Also remove the DNS record from ${domainName}'s zone`}
            description={`Removes the ${selector?.key_name} TXT record so no orphaned public key is left behind.`}
            isChecked={removeRrset}
            onChange={(_event, checked) => setRemoveRrset(checked)}
            isDisabled={isSubmitting}
          />
        </div>
      );
    }
    return (
      <Content component="small" className="pf-v6-u-mt-md">
        {dnsZone ? (
          <>
            This domain&apos;s DNS isn&apos;t managed here, so remember to remove the{' '}
            <strong>{selector?.key_name}</strong> TXT record from DNS to avoid an orphaned public key.
          </>
        ) : (
          <>
            We couldn&apos;t check this domain&apos;s DNS zone, so remember to remove the{' '}
            <strong>{selector?.key_name}</strong> TXT record from DNS to avoid an orphaned public key.
          </>
        )}
      </Content>
    );
  };

  return (
    <Modal variant="small" isOpen={selector !== null} onClose={resetAndClose} aria-label="Delete DKIM selector">
      <ModalHeader title="Delete DKIM selector" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        {selector?.active && (
          <Alert variant="warning" isInline title="This is the active signing selector" className="pf-v6-u-mb-md">
            Deleting it stops DKIM signing for this domain until another selector is activated.
          </Alert>
        )}
        <Content>
          Delete DKIM selector <strong>{selector?.selector}</strong> for <strong>{domainName}</strong>?
          <br />
          <br />
          This removes the key files and OpenDKIM table entries on the mail server.
        </Content>
        {renderDnsSection()}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="danger"
          onClick={handleDelete}
          isLoading={isSubmitting}
          isDisabled={isSubmitting || dnsZoneLoading}
        >
          Delete
        </Button>
        <Button variant="link" onClick={resetAndClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default DkimDeleteModal;
